<template lang="pug">
#send-message-template-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span テンプレート修正
    .box-body.register-body
      FormInput(v-model="name" key-name="テンプレート名" name="name" type="text" placeholder="テンプレート名"
        :maxlength="32" :validation-rules="{ required: true, max: 32 }" required)
      FormInput(v-model="title" key-name="タイトル" name="title" type="text" placeholder="タイトル"
        :maxlength="100" :validation-rules="{ max: 100 }")
      FormTextarea(v-model="text" key-name="本文" name="text" placeholder="本文" :rows="7"
        :maxlength="1000" :validation-rules="{ max: 1000 }" :styles="{ width: '600px' }")
        template(v-slot:question)
          HelpIcon.ml-auto(popoverId="message-url-help" title="リンクの記載について")
            template(v-slot:text)
              span メッセージ内でURLリンクを使用したい場合は、改行してURLを記載することでリンクを送ることができます。
              br
              br
              strong 記載例
              br
              span 以下のリンクからコニカミノルタへアクセスしてください。
              br
              span https://www.konicaminolta.com
              br
              br
              strong リンク名を付ける方法
              br
              span 以下のようにリンク名とURLを記載すると、リンク名を表示することができます。
              br
              span [リンク名](URL)
              br
              br
              strong 記載例（リンク名を付ける方法）
              br
              span [HitomeQ](https://konicaminolta.com)
      .checkboxs.d-flex.flex-row.flex-wrap.mb-2
        FormCheck.option-button.add-read(v-model="addReadButton" key-name="見ましたボタン" name="add-read" label="つける" @onClick="onAddReadCheck(addReadButton)")
        FormCheck.option-button.option-text(v-model="addOptionText" key-name="既読確認メッセージ" name="option-text" label="つける"
          :active="addReadButton"    :disabled="!addReadButton")
          template(v-slot:question)
            HelpIcon.ml-auto(popoverId="message-set-confirm" title="既読確認メッセージについて")
              template(v-slot:text)
                span チェックをつけることでLINEメッセージの見ましたボタンの強調メッセージを付与します。
                br
                span 実際に送られるLINEメッセージは送信ボタン押下時のプレビューをご確認ください。
                br
                br
                strong 付与されるメッセージ
                br
                span.option-text 「見ました」が押されなかった場合、施設から連絡をさせて頂く場合があります。
      FormCheck.option-button.reply-btn(v-model="addReplyButton" key-name="返信ボタン" name="add-reply-button" label="つける")

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoTemplateList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="テンプレートを更新しますか？" @ok="updateTemplate(templateId)")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoTemplateList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendMessagesState } from "@/store/NeedRefreshes";
import CustomValidate from "@/commons/utils/CustomValidate";

import { PcUrl } from "@/commons/constants/PcUrl";

import SendMessageTemplatesService, { SendMessageTemplatesGetModel, SendMessageTemplatesPatchModel, TemplateType } from "@/commons/services/SendMessageTemplates";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormCheck from "@/pc/components/forms/FormCheck.vue";

/**
 * ### メッセージ送信テンプレート登録画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert,
    FormInput,
    FormTextarea,
    FormCheck
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessageTemplateRegister extends Vue {
  /** メッセージ送信テンプレートサービス */
  @Inject("SendMessageTemplatesService") sendMessageTemplatesService!: SendMessageTemplatesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** テンプレートID */
  templateId: number | null = null;
  /** テンプレート名 */
  name: string = "";
  /** メッセージ送信で転記するタイトル */
  title: string | null = null;
  /** メッセージ送信で転記する本文 */
  text: string | null = null;
  /** メッセージ送信で見ましたボタンをつけるか */
  addOptionText: boolean | null = null;
  /** メッセージ送信で既読確認メッセージボタンをつけるか */
  addReadButton: boolean | null = null;
  /** メッセージ送信で返信ボタンをつけるか */
  addReplyButton: boolean | null = null;
  /** 更新日時 */
  updated: number = 0;

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const templateId: number = Number(this.$route.params.id);
    const sendMessageTemplate: SendMessageTemplatesGetModel = await this.getSendMessageTemplate(templateId);
    this.templateId = templateId;
    this.name = sendMessageTemplate.name;
    this.title = sendMessageTemplate.title ? sendMessageTemplate.title : null;
    this.text = sendMessageTemplate.text ? sendMessageTemplate.text : null;
    this.addOptionText = sendMessageTemplate.addOptionText;
    this.addReadButton = sendMessageTemplate.addReadButton;
    this.addReplyButton = sendMessageTemplate.addReplyButton;
    this.updated = sendMessageTemplate.updated;

    this.isBusy = false;

    return;
  }

  /**
   * ### メッセージ送信テンプレートを取得する
   */
  public async getSendMessageTemplate(templateId: number): Promise<SendMessageTemplatesGetModel> {
    const response: SendMessageTemplatesGetModel = await this.sendMessageTemplatesService.get(userStore, templateId);
    return response;
  }

  /**
   * ## 見ましたのチェックボックス押下イベント
   *
   * - オプションテキストを強制的にfalseにする
   */
  public onAddReadCheck(): void {
    if (this.addReadButton == true) {
      this.addOptionText = false;
    }

    return;
  }

  /**
   * ### 更新確認ダイアログを表示する
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### メッセージ送信テンプレートを登録する
   */
  public async updateTemplate(templateId: number): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const requestBody: SendMessageTemplatesPatchModel = this.createRequestBody();
    await this.sendMessageTemplatesService.patch(userStore, templateId, requestBody);

    // メッセージ送信のテンプレートが変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendWaiterMessage(sendMessageState);

    this.isBusy = false;

    this.showUpdateSuccessModal = true;

    return;
  }

  /**
   * ### 登録するリクエストボディを作成する
   */
  public createRequestBody(): SendMessageTemplatesPatchModel {
    return {
      name: this.name,
      templateType: TemplateType.MESSAGE,
      title: this.title ? this.title : null,
      text: this.text ? this.text : null,
      addOptionText: this.addOptionText ? this.addOptionText : false,
      addReadButton: this.addReadButton ? this.addReadButton : false,
      addReplyButton: this.addReplyButton ? this.addReplyButton : false,
      updated: this.updated
    };
  }

  /**
   * ### メッセージ送信テンプレート一覧画面へ遷移する
   */
  public gotoTemplateList(): void {
    this.$router.push(PcUrl.SendMessageTemplateList);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#send-message-template-edit {
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  // テキスト入力領域はリサイズできてしまうとレイアウトが崩れるためリサイズを不可にする
  textarea {
    resize: none;
  }

  // チェックボックスは横一列に並べる
  .checkboxs {
    .option-text {
      width: 240px !important;
      margin-right: 40px;
    }
    .register-row {
      width: 180px;
    }
    .option-button {
      margin-bottom: 10px !important;
    }
  }
}
</style>
